/**
 * Wish Intelligence — SCH Summer Shuttles 2026
 *
 * Route planning and roster intelligence tool.
 * Loads data dynamically from Firestore: campRosters/summer-2026
 * Includes inline import functionality.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { formatDate } from '@/lib/utils';
import {
  Users, MapPin, Calendar, Truck, TrendingUp,
  AlertCircle, CheckCircle, Clock, ChevronDown, ChevronUp,
  Upload, FileText, X,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ParsedRider {
  name: string;
  email: string;
  phone?: string;
  pickup: string;
  loop: 'early' | 'late';
  students: number;
  weeks: number[];
  pickupTime?: string;
  dropoffTime?: string;
  rate: 165 | 75;
  status: 'booked' | 'interested';
  routeName?: string;
  note?: string;
}

type LoopId = 'early' | 'late';

// ─── Loop Definitions ─────────────────────────────────────────────────────────

const earlyStops = ['Passyunk Square', 'Graduate Hospital', 'Washington Square', 'Fairmount'];
const lateStops = ['Manayunk/Roxborough', 'Chestnut Hill'];

const LOOP_META = {
  early: {
    name: 'Early Loop',
    subtitle: '$165 · City & South Philly Hubs',
    rate: 165 as const,
    pickupWindow: '7:15 – 7:45 AM',
    arrivalTime: '~8:15 AM at SCH',
    stopNames: earlyStops,
    stopTimes: ['~7:15 AM', '~7:25 AM', '~7:35 AM', '~7:45 AM'],
    color: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', badge: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
  },
  late: {
    name: 'Late Loop',
    subtitle: '$75 · Northwest Hubs',
    rate: 75 as const,
    pickupWindow: '8:00 – 8:20 AM',
    arrivalTime: '~8:35 AM at SCH',
    stopNames: lateStops,
    stopTimes: ['~8:00 AM', '~8:20 AM'],
    color: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-800', badge: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
  },
};

const WEEKS = [1,2,3,4,5,6,7,8,9,10];

// ─── CSV Parsing ──────────────────────────────────────────────────────────────

function parseCSV(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length < 2) return [];

  const headers = parseCSVRow(lines[0]);
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVRow(lines[i]);
    if (values.length === 0) continue;
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] ?? '';
    });
    rows.push(row);
  }

  return rows;
}

function parseCSVRow(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

function parseWeeks(raw: string): number[] {
  if (!raw || raw.trim() === '') return [];
  return raw
    .split(',')
    .map(s => parseInt(s.trim(), 10))
    .filter(n => !isNaN(n));
}

function parseRate(raw: string): 165 | 75 {
  const n = parseInt(raw.replace(/[^0-9]/g, ''), 10);
  return n === 75 ? 75 : 165;
}

function isSkippableTime(val: string): boolean {
  return val.trim().toLowerCase().includes('please let us know');
}

// ─── Parse Booked Students CSV ────────────────────────────────────────────────

function parseBookedCSV(rows: Record<string, string>[]): ParsedRider[] {
  return rows.map(row => {
    const rate = parseRate(row['Weekly Rate Per Student'] ?? '');
    return {
      name: row['Guardian Name'] ?? '',
      email: row['Guardian Email'] ?? '',
      phone: row['Guardian Phone'] ?? undefined,
      pickup: row['Pickup Location'] ?? '',
      pickupTime: row['Pickup Time'] ?? undefined,
      dropoffTime: row['Dropoff Time'] ?? undefined,
      students: parseInt(row['Students'] ?? '1', 10) || 1,
      weeks: parseWeeks(row['Enrolled Weeks'] ?? ''),
      rate,
      loop: rate === 75 ? 'late' : 'early',
      status: 'booked' as const,
      routeName: row['Route Name'] ?? undefined,
    };
  }).filter(r => r.name || r.email);
}

// ─── Parse Interested Families CSV ───────────────────────────────────────────

function parseInterestedCSV(rows: Record<string, string>[]): ParsedRider[] {
  const seen = new Set<string>();
  const results: ParsedRider[] = [];

  for (const row of rows) {
    const email = row['Recipient Email'] ?? '';
    if (!email || seen.has(email)) continue;
    seen.add(email);

    const rate = parseRate(row['Weekly Rate'] ?? '');
    const pickupTimeRaw = row['Pickup Time'] ?? '';
    const dropoffTimeRaw = row['Dropoff Time'] ?? '';

    results.push({
      name: row['Recipient Name'] ?? '',
      email,
      pickup: row['Pickup Location'] ?? '',
      pickupTime: isSkippableTime(pickupTimeRaw) ? undefined : pickupTimeRaw || undefined,
      dropoffTime: isSkippableTime(dropoffTimeRaw) ? undefined : dropoffTimeRaw || undefined,
      students: parseInt(row['Students'] ?? '1', 10) || 1,
      weeks: parseWeeks(row['Selected Weeks'] ?? ''),
      rate,
      loop: rate === 75 ? 'late' : 'early',
      status: 'interested' as const,
      routeName: row['Route Name'] ?? undefined,
    });
  }

  return results;
}

// ─── Preview Table ────────────────────────────────────────────────────────────

function PreviewTable({ riders, label }: { riders: ParsedRider[]; label: string }) {
  if (riders.length === 0) return null;

  return (
    <div className="mt-4">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        {label} — {riders.length} records
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Name</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Email</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Pickup</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Students</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Weeks</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Rate</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Loop</th>
            </tr>
          </thead>
          <tbody>
            {riders.map((r, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-900">{r.name || <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2 text-gray-600">{r.email}</td>
                <td className="px-3 py-2 text-gray-600">{r.pickup || <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2 text-center text-gray-900">{r.students}</td>
                <td className="px-3 py-2 text-center text-gray-600">
                  {r.weeks.length > 0 ? r.weeks.join(', ') : <span className="text-gray-400">TBD</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${r.rate === 75 ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                    ${r.rate}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${r.loop === 'late' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                    {r.loop}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Upload Zone ──────────────────────────────────────────────────────────────

function UploadZone({
  label,
  description,
  fileName,
  rowCount,
  error,
  onFile,
  onClear,
  color,
}: {
  label: string;
  description: string;
  fileName: string | null;
  rowCount: number;
  error: string | null;
  onFile: (file: File) => void;
  onClear: () => void;
  color: 'blue' | 'green';
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const colorMap = {
    blue: {
      border: 'border-blue-300 hover:border-blue-400',
      bg: 'bg-blue-50 hover:bg-blue-100',
      text: 'text-blue-700',
      badge: 'bg-blue-100 text-blue-800',
    },
    green: {
      border: 'border-green-300 hover:border-green-400',
      bg: 'bg-green-50 hover:bg-green-100',
      text: 'text-green-700',
      badge: 'bg-green-100 text-green-800',
    },
  };
  const c = colorMap[color];

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  };

  return (
    <div className="flex-1">
      <div className="text-sm font-semibold text-gray-700 mb-2">{label}</div>
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
          fileName ? 'border-gray-300 bg-white' : `${c.border} ${c.bg}`
        }`}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => !fileName && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = '';
          }}
        />
        {fileName ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div className="text-left min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{fileName}</div>
                <div className="text-xs text-gray-500">{rowCount} records parsed</div>
              </div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); onClear(); }}
              className="p-1 hover:bg-gray-100 rounded flex-shrink-0"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        ) : (
          <>
            <Upload className={`w-8 h-8 mx-auto mb-2 ${c.text}`} />
            <div className={`text-sm font-medium ${c.text}`}>Drop CSV here or click to browse</div>
            <div className="text-xs text-gray-500 mt-1">{description}</div>
          </>
        )}
      </div>
      {error && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function studentsOnLoopWeek(loop: LoopId, week: number, riders: ParsedRider[]) {
  return riders
    .filter(r => r.loop === loop && r.weeks.includes(week))
    .reduce((sum, r) => sum + r.students, 0);
}

function ridersOnLoopWeek(loop: LoopId, week: number, riders: ParsedRider[]) {
  return riders.filter(r => r.loop === loop && r.weeks.includes(week));
}

function driversNeeded(students: number) {
  if (students === 0) return 0;
  return students <= 14 ? 1 : 2;
}

function stopMatchesRider(stopName: string, pickup: string): boolean {
  return pickup.toLowerCase().includes(stopName.toLowerCase()) ||
    stopName.toLowerCase().includes(pickup.toLowerCase());
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ icon, value, label, sub, color }: {
  icon: React.ReactNode; value: string | number; label: string; sub?: string; color: string;
}) {
  return (
    <Card>
      <CardContent className="p-5 flex items-start gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
          {icon}
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-sm font-medium text-gray-700">{label}</div>
          {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function RiderRow({ rider }: { rider: ParsedRider }) {
  return (
    <div className={`flex items-start gap-3 px-3 py-2 rounded-lg ${rider.status === 'booked' ? 'bg-white' : 'bg-yellow-50 border border-yellow-200'}`}>
      <div className="flex-shrink-0 mt-0.5">
        {rider.status === 'booked'
          ? <CheckCircle className="w-4 h-4 text-green-600" />
          : <Clock className="w-4 h-4 text-yellow-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-gray-900">{rider.name}</span>
          <span className="text-xs text-gray-500">{rider.email}</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
            {rider.students} student{rider.students !== 1 ? 's' : ''}
          </span>
          {rider.pickupTime && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />{rider.pickupTime}
            </span>
          )}
        </div>
        {rider.weeks.length > 0 && (
          <div className="text-xs text-gray-500 mt-0.5">
            Weeks: {rider.weeks.join(', ')}
          </div>
        )}
        {rider.note && (
          <div className="text-xs text-amber-700 mt-1 flex items-start gap-1">
            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            {rider.note}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Import Panel ─────────────────────────────────────────────────────────────

function ImportPanel({
  bookedFileName,
  interestedFileName,
  bookedError,
  interestedError,
  parsedBooked,
  parsedInterested,
  saving,
  onBookedFile,
  onInterestedFile,
  onClearBooked,
  onClearInterested,
  onSave,
}: {
  bookedFileName: string | null;
  interestedFileName: string | null;
  bookedError: string | null;
  interestedError: string | null;
  parsedBooked: ParsedRider[];
  parsedInterested: ParsedRider[];
  saving: boolean;
  onBookedFile: (file: File) => void;
  onInterestedFile: (file: File) => void;
  onClearBooked: () => void;
  onClearInterested: () => void;
  onSave: () => void;
}) {
  const hasAnyData = parsedBooked.length > 0 || parsedInterested.length > 0;

  return (
    <Card className="mb-8 border-shuttlebee-yellow/40">
      <CardHeader className="pb-2 bg-amber-50 rounded-t-lg">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-shuttlebee-yellow rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <FileText className="w-5 h-5 text-gray-900" />
          </div>
          <div>
            <CardTitle className="text-base">SCH Summer Camp 2026 — Import Roster</CardTitle>
            <p className="text-sm text-gray-600 mt-0.5">
              Upload the Booked Customers CSV and the Interested Families CSV exported from Route Offers
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4 mb-2">
          <UploadZone
            label="Booked Students"
            description="Export from Enrollments — includes Guardian Name, Enrolled Weeks, Weekly Rate Per Student"
            fileName={bookedFileName}
            rowCount={parsedBooked.length}
            error={bookedError}
            onFile={onBookedFile}
            onClear={onClearBooked}
            color="blue"
          />
          <UploadZone
            label="Interested Families"
            description="Export from Route Offers — includes Recipient Name, Selected Weeks, Weekly Rate"
            fileName={interestedFileName}
            rowCount={parsedInterested.length}
            error={interestedError}
            onFile={onInterestedFile}
            onClear={onClearInterested}
            color="green"
          />
        </div>

        {hasAnyData && (
          <>
            <PreviewTable riders={parsedBooked} label="Booked Students" />
            <PreviewTable riders={parsedInterested} label="Interested Families" />
          </>
        )}

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <div className="text-sm text-gray-600">
            {hasAnyData
              ? `Ready to save: ${parsedBooked.length} booked · ${parsedInterested.length} interested`
              : 'Upload at least one CSV to continue'}
          </div>
          <Button
            onClick={onSave}
            disabled={!hasAnyData || saving}
            className="flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Save to Firestore
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function WishIntelligence() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { toast } = useToast();

  // Firestore data state
  const [bookedRiders, setBookedRiders] = useState<ParsedRider[]>([]);
  const [interestedRiders, setInterestedRiders] = useState<ParsedRider[]>([]);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [expandedWeek, setExpandedWeek] = useState<number | null>(null);

  // Import panel state
  const [showImport, setShowImport] = useState(false);
  const [bookedFileName, setBookedFileName] = useState<string | null>(null);
  const [interestedFileName, setInterestedFileName] = useState<string | null>(null);
  const [bookedError, setBookedError] = useState<string | null>(null);
  const [interestedError, setInterestedError] = useState<string | null>(null);
  const [parsedBooked, setParsedBooked] = useState<ParsedRider[]>([]);
  const [parsedInterested, setParsedInterested] = useState<ParsedRider[]>([]);
  const [saving, setSaving] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (role && role !== 'admin') {
      navigate('/dashboard');
    }
  }, [role, navigate]);

  // Load data from Firestore
  useEffect(() => {
    if (!user || role !== 'admin') return;

    const loadData = async () => {
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, 'campRosters', 'summer-2026'));
        if (snap.exists()) {
          const data = snap.data();
          setBookedRiders(data.bookedRiders ?? []);
          setInterestedRiders(data.interestedRiders ?? []);
          if (data.updatedAt instanceof Timestamp) {
            setUpdatedAt(data.updatedAt.toDate());
          }
          setHasData(true);
          setShowImport(false);
        } else {
          setHasData(false);
          setShowImport(true);
        }
      } catch (err) {
        console.error('[WishIntelligence] Error loading data:', err);
        setHasData(false);
        setShowImport(true);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, role]);

  // ─── Import handlers ───────────────────────────────────────────────────────

  const handleBookedFile = (file: File) => {
    setBookedError(null);
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const rows = parseCSV(text);
        const riders = parseBookedCSV(rows);
        if (riders.length === 0) {
          setBookedError('No valid records found. Check that the CSV has the expected headers.');
          return;
        }
        setParsedBooked(riders);
        setBookedFileName(file.name);
      } catch (err) {
        console.error('Error parsing booked CSV:', err);
        setBookedError('Failed to parse CSV. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  const handleInterestedFile = (file: File) => {
    setInterestedError(null);
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const rows = parseCSV(text);
        const riders = parseInterestedCSV(rows);
        if (riders.length === 0) {
          setInterestedError('No valid records found. Check that the CSV has the expected headers.');
          return;
        }
        setParsedInterested(riders);
        setInterestedFileName(file.name);
      } catch (err) {
        console.error('Error parsing interested CSV:', err);
        setInterestedError('Failed to parse CSV. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  const clearBooked = () => {
    setParsedBooked([]);
    setBookedFileName(null);
    setBookedError(null);
  };

  const clearInterested = () => {
    setParsedInterested([]);
    setInterestedFileName(null);
    setInterestedError(null);
  };

  const handleSave = async () => {
    if (!user) return;
    if (parsedBooked.length === 0 && parsedInterested.length === 0) {
      toast({
        title: 'No data to save',
        description: 'Upload at least one CSV before saving.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      await setDoc(doc(db, 'campRosters', 'summer-2026'), {
        season: 'Summer 2026',
        bookedRiders: parsedBooked,
        interestedRiders: parsedInterested,
        updatedAt: serverTimestamp(),
        importedBy: user.uid,
      });
      // Update local state directly — no page reload needed
      setBookedRiders(parsedBooked);
      setInterestedRiders(parsedInterested);
      setUpdatedAt(new Date());
      setHasData(true);
      setShowImport(false);
      // Reset import staging state
      setParsedBooked([]);
      setParsedInterested([]);
      setBookedFileName(null);
      setInterestedFileName(null);
      toast({
        title: 'Saved!',
        description: `${parsedBooked.length} booked and ${parsedInterested.length} interested riders saved to Firestore.`,
      });
    } catch (err) {
      console.error('Error saving to Firestore:', err);
      toast({
        title: 'Save failed',
        description: 'Could not save to Firestore. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // ─── Loading state ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading roster data...</p>
        </div>
      </div>
    );
  }

  // ─── Shared page scaffold (used for both empty and data states) ────────────

  // ─── Computed Values (only meaningful when hasData) ────────────────────────

  const allRiders = [...bookedRiders, ...interestedRiders];

  const totalBookedStudents = bookedRiders.reduce((sum, r) => sum + r.students, 0);
  const totalInterestedStudents = interestedRiders.reduce((sum, r) => sum + r.students, 0);

  const totalDriverWeeks = WEEKS.reduce((sum, week) => {
    const earlyStudents = studentsOnLoopWeek('early', week, bookedRiders);
    const lateStudents = studentsOnLoopWeek('late', week, bookedRiders);
    return sum + driversNeeded(earlyStudents) + driversNeeded(lateStudents);
  }, 0);

  const peakWeek = WEEKS.reduce((best, week) => {
    const students = WEEKS.map(w => studentsOnLoopWeek('early', w, bookedRiders) + studentsOnLoopWeek('late', w, bookedRiders));
    return students[week - 1] > students[best - 1] ? week : best;
  }, 1);
  const peakStudents = studentsOnLoopWeek('early', peakWeek, bookedRiders) + studentsOnLoopWeek('late', peakWeek, bookedRiders);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-8 h-8 bg-shuttlebee-yellow rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-gray-900" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Wish Intelligence</h1>
            </div>
            <p className="text-gray-600 ml-11">
              SCH Summer Shuttles 2026 · Route planning, roster, and growth projections
            </p>
            {hasData && (
              <div className="ml-11 mt-1 text-xs text-gray-400">
                {updatedAt ? `Last updated ${formatDate(updatedAt)} · ` : ''}
                {bookedRiders.length} booked families · {interestedRiders.length} interested families
              </div>
            )}
            {!hasData && (
              <div className="ml-11 mt-1 text-sm text-gray-500">
                Import your roster data below to get started with route planning and analytics.
              </div>
            )}
          </div>
          {hasData && (
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => setShowImport(v => !v)}
            >
              <Upload className="w-4 h-4" />
              Import / Update Data
              {showImport ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          )}
        </div>

        {/* Import Panel — shown inline when toggled (or always when no data) */}
        {showImport && (
          <ImportPanel
            bookedFileName={bookedFileName}
            interestedFileName={interestedFileName}
            bookedError={bookedError}
            interestedError={interestedError}
            parsedBooked={parsedBooked}
            parsedInterested={parsedInterested}
            saving={saving}
            onBookedFile={handleBookedFile}
            onInterestedFile={handleInterestedFile}
            onClearBooked={clearBooked}
            onClearInterested={clearInterested}
            onSave={handleSave}
          />
        )}

        {/* Only render analytics content when there's data */}
        {hasData && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard
                icon={<CheckCircle className="w-5 h-5 text-white" />}
                value={totalBookedStudents}
                label="Students Booked"
                sub={`${bookedRiders.length} families confirmed`}
                color="bg-green-500"
              />
              <StatCard
                icon={<Clock className="w-5 h-5 text-white" />}
                value={totalInterestedStudents}
                label="Students Interested"
                sub={`${interestedRiders.length} families pending`}
                color="bg-yellow-500"
              />
              <StatCard
                icon={<Truck className="w-5 h-5 text-white" />}
                value={totalDriverWeeks}
                label="Driver-Weeks Needed"
                sub="Based on confirmed bookings"
                color="bg-blue-500"
              />
              <StatCard
                icon={<TrendingUp className="w-5 h-5 text-white" />}
                value={`Wk ${peakWeek}`}
                label="Peak Week"
                sub={`${peakStudents} students on the road`}
                color="bg-purple-500"
              />
            </div>

            {/* Route Loops */}
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Route Loops</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {(['early', 'late'] as LoopId[]).map(loopId => {
                const loop = LOOP_META[loopId];
                const loopBooked = bookedRiders.filter(r => r.loop === loopId);
                const loopInterested = interestedRiders.filter(r => r.loop === loopId);
                const totalBookedOnLoop = loopBooked.reduce((s, r) => s + r.students, 0);
                const totalInterestedOnLoop = loopInterested.reduce((s, r) => s + r.students, 0);

                const activeStops = loop.stopNames.map((stopName, i) => {
                  const hasBooked = loopBooked.some(r => stopMatchesRider(stopName, r.pickup));
                  const hasInterested = loopInterested.some(r => stopMatchesRider(stopName, r.pickup));
                  return { name: stopName, time: loop.stopTimes[i] ?? '', hasBooked, hasInterested };
                });

                return (
                  <Card key={loopId} className={`border-2 ${loop.color.border}`}>
                    <CardHeader className={`pb-3 ${loop.color.bg} rounded-t-lg`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className={`text-lg ${loop.color.text}`}>{loop.name}</CardTitle>
                          <div className={`text-sm font-medium ${loop.color.text} opacity-80`}>{loop.subtitle}</div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${loop.color.text}`}>{totalBookedOnLoop}</div>
                          <div className={`text-xs ${loop.color.text} opacity-70`}>booked students</div>
                        </div>
                      </div>
                      <div className="flex gap-4 mt-2 text-xs text-gray-600">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Pickup {loop.pickupWindow}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {loop.arrivalTime}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4">
                      {/* Stops */}
                      <div className="mb-4">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Morning Pickup Order</div>
                        <div className="space-y-1">
                          {activeStops.map((stop, i) => (
                            <div key={stop.name} className="flex items-center gap-2">
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${stop.hasBooked ? loop.color.dot : 'bg-gray-300'}`}>
                                {i + 1}
                              </div>
                              <div className="flex-1">
                                <span className={`text-sm font-medium ${stop.hasBooked || stop.hasInterested ? 'text-gray-900' : 'text-gray-400'}`}>
                                  {stop.name}
                                </span>
                                <span className="text-xs text-gray-400 ml-2">{stop.time}</span>
                              </div>
                              <div className="flex gap-1">
                                {stop.hasBooked && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">booked</span>}
                                {stop.hasInterested && !stop.hasBooked && <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">potential</span>}
                                {stop.hasInterested && stop.hasBooked && <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">+interested</span>}
                              </div>
                            </div>
                          ))}
                          <div className="flex items-center gap-2 mt-1">
                            <div className="w-5 h-5 rounded-full bg-shuttlebee-yellow flex items-center justify-center flex-shrink-0">
                              <MapPin className="w-3 h-3 text-gray-900" />
                            </div>
                            <span className="text-sm font-semibold text-gray-700">SCH · 8000 Cherokee St</span>
                          </div>
                        </div>
                      </div>

                      {/* Booked Riders */}
                      <div className="mb-3">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Confirmed Families ({loopBooked.length})
                        </div>
                        <div className="space-y-1">
                          {loopBooked.map(r => <RiderRow key={r.email + r.pickup} rider={r} />)}
                        </div>
                      </div>

                      {/* Interested Riders */}
                      {loopInterested.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                            Interested — Not Yet Booked ({loopInterested.length} families · {totalInterestedOnLoop} students)
                          </div>
                          <div className="space-y-1">
                            {loopInterested.map(r => <RiderRow key={r.email + r.pickup} rider={r} />)}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Weekly Roster */}
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Weekly Roster &amp; Driver Needs</h2>
            <Card className="mb-8">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-3 font-semibold text-gray-700 w-20">Week</th>
                        <th className="text-center px-3 py-3 font-semibold text-blue-700">Early Loop<br /><span className="font-normal text-xs text-gray-500">$165 · students</span></th>
                        <th className="text-center px-3 py-3 font-semibold text-green-700">Late Loop<br /><span className="font-normal text-xs text-gray-500">$75 · students</span></th>
                        <th className="text-center px-3 py-3 font-semibold text-gray-700">Total<br /><span className="font-normal text-xs text-gray-500">students</span></th>
                        <th className="text-center px-3 py-3 font-semibold text-gray-700">Drivers<br /><span className="font-normal text-xs text-gray-500">needed</span></th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Families on road</th>
                        <th className="w-8 px-2 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {WEEKS.map(week => {
                        const earlyStudents = studentsOnLoopWeek('early', week, bookedRiders);
                        const lateStudents = studentsOnLoopWeek('late', week, bookedRiders);
                        const total = earlyStudents + lateStudents;
                        const drivers = driversNeeded(earlyStudents) + driversNeeded(lateStudents);
                        const earlyRidersWeek = ridersOnLoopWeek('early', week, bookedRiders);
                        const lateRidersWeek = ridersOnLoopWeek('late', week, bookedRiders);
                        const allWeekRiders = [...earlyRidersWeek, ...lateRidersWeek];
                        const isExpanded = expandedWeek === week;

                        return (
                          <React.Fragment key={week}>
                            <tr
                              className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${total === 0 ? 'opacity-50' : ''}`}
                              onClick={() => setExpandedWeek(isExpanded ? null : week)}
                            >
                              <td className="px-4 py-3 font-semibold text-gray-900">Week {week}</td>
                              <td className="px-3 py-3 text-center">
                                {earlyStudents > 0
                                  ? <span className="inline-block bg-blue-100 text-blue-800 font-semibold px-2 py-0.5 rounded text-xs">{earlyStudents}</span>
                                  : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-3 py-3 text-center">
                                {lateStudents > 0
                                  ? <span className="inline-block bg-green-100 text-green-800 font-semibold px-2 py-0.5 rounded text-xs">{lateStudents}</span>
                                  : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-3 py-3 text-center font-semibold text-gray-900">
                                {total > 0 ? total : <span className="text-gray-300">0</span>}
                              </td>
                              <td className="px-3 py-3 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {Array.from({ length: drivers }).map((_, i) => (
                                    <Truck key={i} className="w-4 h-4 text-gray-600" />
                                  ))}
                                  {drivers === 0 && <span className="text-gray-300 text-xs">none</span>}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-600 text-xs">
                                {allWeekRiders.map(r => r.name.split(' ')[0]).join(', ') || '—'}
                              </td>
                              <td className="px-2 py-3 text-gray-400">
                                {total > 0 && (isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                              </td>
                            </tr>
                            {isExpanded && total > 0 && (
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <td colSpan={7} className="px-6 py-3">
                                  <div className="grid md:grid-cols-2 gap-4">
                                    {earlyRidersWeek.length > 0 && (
                                      <div>
                                        <div className="text-xs font-semibold text-blue-700 mb-2">Early Loop</div>
                                        <div className="space-y-1">
                                          {earlyRidersWeek.map(r => <RiderRow key={r.email} rider={r} />)}
                                        </div>
                                      </div>
                                    )}
                                    {lateRidersWeek.length > 0 && (
                                      <div>
                                        <div className="text-xs font-semibold text-green-700 mb-2">Late Loop</div>
                                        <div className="space-y-1">
                                          {lateRidersWeek.map(r => <RiderRow key={r.email} rider={r} />)}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t-2 border-gray-300">
                        <td className="px-4 py-3 font-bold text-gray-900">Season Total</td>
                        <td className="px-3 py-3 text-center font-bold text-blue-800">
                          {WEEKS.reduce((s, w) => s + studentsOnLoopWeek('early', w, bookedRiders), 0)}
                        </td>
                        <td className="px-3 py-3 text-center font-bold text-green-800">
                          {WEEKS.reduce((s, w) => s + studentsOnLoopWeek('late', w, bookedRiders), 0)}
                        </td>
                        <td className="px-3 py-3 text-center font-bold text-gray-900">
                          {WEEKS.reduce((s, w) => s + studentsOnLoopWeek('early', w, bookedRiders) + studentsOnLoopWeek('late', w, bookedRiders), 0)}
                        </td>
                        <td className="px-3 py-3 text-center font-bold text-gray-900">{totalDriverWeeks}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Growth Projections */}
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Growth Projections</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">If all interested families book</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Early Loop adds</span>
                      <span className="font-semibold text-blue-800">
                        +{interestedRiders.filter(r => r.loop === 'early').reduce((s, r) => s + r.students, 0)} students
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Late Loop adds</span>
                      <span className="font-semibold text-green-800">
                        +{interestedRiders.filter(r => r.loop === 'late').reduce((s, r) => s + r.students, 0)} students
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-t pt-3">
                      <span className="text-sm font-semibold text-gray-700">Total potential students</span>
                      <span className="font-bold text-gray-900">
                        {totalBookedStudents + totalInterestedStudents}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Items Needing Attention</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {allRiders
                      .filter(r => r.note)
                      .map((r, i) => (
                        <div key={i} className={`text-xs p-2 rounded border ${r.note?.startsWith('⚠') ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-green-700 bg-green-50 border-green-200'}`}>
                          {r.note}
                        </div>
                      ))}
                    {allRiders.filter(r => r.note).length === 0 && (
                      <div className="text-sm text-gray-500">No attention items flagged.</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Driver Roster Summary */}
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Driver Roster Summary</h2>
            <Card className="mb-8">
              <CardContent className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">Week</th>
                        <th className="text-center py-2 px-3 font-semibold text-blue-700">Early Loop Driver</th>
                        <th className="text-center py-2 px-3 font-semibold text-green-700">Late Loop Driver</th>
                        <th className="text-center py-2 px-3 font-semibold text-gray-700">Total Drivers</th>
                        <th className="text-center py-2 px-3 font-semibold text-gray-700">Early Students</th>
                        <th className="text-center py-2 px-3 font-semibold text-gray-700">Late Students</th>
                      </tr>
                    </thead>
                    <tbody>
                      {WEEKS.map(week => {
                        const earlyStudents = studentsOnLoopWeek('early', week, bookedRiders);
                        const lateStudents = studentsOnLoopWeek('late', week, bookedRiders);
                        const earlyDrivers = driversNeeded(earlyStudents);
                        const lateDrivers = driversNeeded(lateStudents);
                        const totalDrivers = earlyDrivers + lateDrivers;

                        return (
                          <tr key={week} className={`border-b border-gray-100 ${totalDrivers === 0 ? 'opacity-40' : ''}`}>
                            <td className="py-2 px-3 font-medium text-gray-900">Week {week}</td>
                            <td className="py-2 px-3 text-center">
                              {earlyDrivers > 0
                                ? <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium"><Truck className="w-3 h-3" /> TBD</span>
                                : <span className="text-gray-300 text-xs">not needed</span>}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {lateDrivers > 0
                                ? <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium"><Truck className="w-3 h-3" /> TBD</span>
                                : <span className="text-gray-300 text-xs">not needed</span>}
                            </td>
                            <td className="py-2 px-3 text-center font-bold text-gray-900">
                              {totalDrivers > 0 ? totalDrivers : <span className="text-gray-300">0</span>}
                            </td>
                            <td className="py-2 px-3 text-center text-gray-600">{earlyStudents || '—'}</td>
                            <td className="py-2 px-3 text-center text-gray-600">{lateStudents || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t-2 border-gray-300 font-bold">
                        <td className="py-2 px-3 text-gray-900">Season Total</td>
                        <td colSpan={2} className="py-2 px-3 text-center text-gray-600 text-xs font-normal">
                          Assign drivers before Week 1
                        </td>
                        <td className="py-2 px-3 text-center text-gray-900">{totalDriverWeeks}</td>
                        <td className="py-2 px-3 text-center text-gray-600">
                          {WEEKS.reduce((s, w) => s + studentsOnLoopWeek('early', w, bookedRiders), 0)}
                        </td>
                        <td className="py-2 px-3 text-center text-gray-600">
                          {WEEKS.reduce((s, w) => s + studentsOnLoopWeek('late', w, bookedRiders), 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="mt-3 text-xs text-gray-500 flex items-start gap-1">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  Driver slots marked "TBD" — assign specific drivers as roster fills in. 1 driver per loop unless &gt;14 students (then 2).
                </div>
              </CardContent>
            </Card>

            <div className="text-xs text-gray-400 text-center pb-4">
              Wish Intelligence v1.0 · SCH Summer Shuttles 2026 · Data loaded from Firestore
              {updatedAt ? ` · Last import ${formatDate(updatedAt)}` : ''}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
